import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { RedImmobile } from '../../../types/red'
import { redApi } from '../../../services/api'

const TabReportAnac: React.FC<{ immobile: RedImmobile; onUpdate: () => void }> = ({ immobile }) => {
  const [report, setReport] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    redApi.reportAnac.immobile(immobile.id)
      .then(res => { setReport(res.data); setLoading(false) })
      .catch(() => { toast.error('Errore caricamento report'); setLoading(false) })
  }, [immobile.id])

  if (loading) return <div className="text-gray-400">Caricamento report ANAC/USR...</div>
  if (!report) return <div className="text-gray-400">Nessun dato disponibile.</div>

  const { immobile: ri, acquisizione, usr, appalto, sal, pagamenti, documenti, pratiche_edilizie, rendicontazioni, finanziamenti } = report

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-800">📋 Report ANAC/USR</h2>
        <span className="text-xs text-gray-400">Generato il {new Date(report.data_generazione).toLocaleDateString('it-IT')}</span>
      </div>

      {/* Dati immobile */}
      <Section title="Dati Immobile">
        <Grid>
          <KV label="Nome" value={ri.nome_riferimento} />
          <KV label="Indirizzo" value={`${ri.indirizzo || ''}, ${ri.comune || ''} (${ri.provincia || ''})`} />
          <KV label="Catasto" value={`F.${ri.foglio} P.${ri.particella} S.${ri.subalterno}`} />
          <KV label="Categoria" value={ri.categoria_catastale} />
          <KV label="Superficie" value={ri.superficie_mq ? `${ri.superficie_mq} mq` : '—'} />
          <KV label="Fase" value={ri.fase_corrente} />
        </Grid>
      </Section>

      {/* Acquisizione */}
      <Section title="Acquisizione">
        <Grid>
          <KV label="Prezzo acquisto" value={fmtEur(acquisizione.prezzo_acquisto)} />
          <KV label="Imposte" value={fmtEur(acquisizione.totale_imposte)} />
          <KV label="Spese notarili" value={fmtEur(acquisizione.totale_spese_notarili)} />
          <KV label="Costo totale" value={fmtEur(acquisizione.costo_totale_acquisizione)} bold />
        </Grid>
      </Section>

      {/* USR */}
      {usr.numero_pratica && (
        <Section title="Contributo USR">
          <Grid>
            <KV label="N° pratica" value={usr.numero_pratica} />
            <KV label="Importo approvato" value={fmtEur(usr.importo_approvato)} />
            <KV label="Quota %" value={usr.quota_percentuale ? `${usr.quota_percentuale}%` : '—'} />
            <KV label="Finanziamento totale" value={fmtEur(usr.finanziamento_usr_totale)} bold />
          </Grid>
        </Section>
      )}

      {/* Finanziamenti */}
      {finanziamenti?.length > 0 && (
        <Section title="Finanziamenti">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-200 text-left text-xs text-gray-500">
              <th className="py-2">Tipo</th><th>Istituto</th><th>Importo</th><th>Tasso</th><th>Erogazione</th>
            </tr></thead>
            <tbody>
              {finanziamenti.map((f: any, i: number) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-2">{f.tipo}</td>
                  <td>{f.istituto || '—'}</td>
                  <td>{fmtEur(f.importo)}</td>
                  <td>{f.tasso_interesse ? `${f.tasso_interesse}%` : '—'}</td>
                  <td>{f.data_erogazione ? new Date(f.data_erogazione).toLocaleDateString('it-IT') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {/* Appalto */}
      {appalto && (
        <Section title="Appalto">
          <Grid>
            <KV label="Appaltatore" value={appalto.appaltatore} />
            <KV label="Importo contratto" value={fmtEur(appalto.importo_contratto)} />
            <KV label="Inizio prevista" value={fmtDate(appalto.data_inizio_prevista)} />
            <KV label="Fine prevista" value={fmtDate(appalto.data_fine_prevista)} />
            <KV label="Stato" value={appalto.stato} />
          </Grid>
        </Section>
      )}

      {/* SAL */}
      {sal?.length > 0 && (
        <Section title="SAL">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-200 text-left text-xs text-gray-500">
              <th className="py-2">N°</th><th>Importo</th><th>Emissione</th><th>Benestare DL</th><th>Richiesto USR</th><th>Erogato USR</th><th>Stato</th>
            </tr></thead>
            <tbody>
              {sal.map((s: any, i: number) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-2">{s.numero}</td>
                  <td>{fmtEur(s.importo)}</td>
                  <td>{fmtDate(s.data_emissione)}</td>
                  <td>{s.benestare_dl ? 'Sì' : 'No'}</td>
                  <td>{fmtEur(s.importo_richiesto_usr)}</td>
                  <td>{fmtEur(s.importo_erogato_usr)}</td>
                  <td>{s.stato}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {/* Pagamenti */}
      {pagamenti?.length > 0 && (
        <Section title="Pagamenti">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-200 text-left text-xs text-gray-500">
              <th className="py-2">Tipologia</th><th>Importo</th><th>Data prevista</th><th>Data effettiva</th><th>Stato</th>
            </tr></thead>
            <tbody>
              {pagamenti.map((p: any, i: number) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-2">{p.tipologia?.replace(/_/g, ' ')}</td>
                  <td>{fmtEur(p.importo)}</td>
                  <td>{fmtDate(p.data_prevista)}</td>
                  <td>{fmtDate(p.data_effettiva)}</td>
                  <td>{p.stato}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {/* Pratiche */}
      {pratiche_edilizie?.length > 0 && (
        <Section title="Pratiche Edilizie">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-200 text-left text-xs text-gray-500">
              <th className="py-2">Tipo</th><th>N°</th><th>Stato</th><th>Presentazione</th><th>Rilascio</th>
            </tr></thead>
            <tbody>
              {pratiche_edilizie.map((p: any, i: number) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-2">{p.tipo}</td>
                  <td>{p.numero || '—'}</td>
                  <td>{p.stato}</td>
                  <td>{fmtDate(p.data_presentazione)}</td>
                  <td>{fmtDate(p.data_rilascio)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {/* Rendicontazioni */}
      {rendicontazioni?.length > 0 && (
        <Section title="Rendicontazioni USR/ANAC">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-200 text-left text-xs text-gray-500">
              <th className="py-2">Periodo</th><th>Importo rendicontato</th><th>Importo approvato</th><th>Stato</th>
            </tr></thead>
            <tbody>
              {rendicontazioni.map((r: any, i: number) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-2">{fmtDate(r.periodo_inizio)} – {fmtDate(r.periodo_fine)}</td>
                  <td>{fmtEur(r.importo_rendicontato)}</td>
                  <td>{fmtEur(r.importo_approvato)}</td>
                  <td>{r.stato}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {/* Documenti */}
      {documenti?.length > 0 && (
        <Section title="Documenti">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-200 text-left text-xs text-gray-500">
              <th className="py-2">Tipologia</th><th>Titolo</th><th>Fase</th><th>Caricato</th><th>Stato</th>
            </tr></thead>
            <tbody>
              {documenti.map((d: any, i: number) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-2">{d.tipologia}</td>
                  <td>{d.titolo || '—'}</td>
                  <td>{d.fase}</td>
                  <td>{d.caricato ? '✅' : '❌'}</td>
                  <td>{d.stato_workflow}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}
    </div>
  )
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="card p-5">
    <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">{title}</h3>
    {children}
  </div>
)

const Grid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{children}</div>
)

const KV: React.FC<{ label: string; value: string | number | null; bold?: boolean }> = ({ label, value, bold }) => (
  <div>
    <div className="text-xs text-gray-400">{label}</div>
    <div className={`text-sm ${bold ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>{value ?? '—'}</div>
  </div>
)

const fmtEur = (v: number | null) => v != null ? `€ ${v.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'
const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString('it-IT') : '—'

export default TabReportAnac
